import { getTranslation, Lang } from "@/data/translation";
import {
  FinancialIndicatorData,
  financialIndicatorResponseSchema,
} from "@/services/financial-indicator";

export type ExtractedData = {
  labels: string[];
  values: {
    key: string;
    value: (number | null)[];
  }[];
};

type ExtractDataParams = {
  lang: Lang;
  data: FinancialIndicatorData;
};

export const extractFinancialIndicatorData = ({
  lang,
  data,
}: ExtractDataParams): ExtractedData => {
  const { t } = getTranslation({ lang });

  const normalChartDataSchema =
    financialIndicatorResponseSchema.options[0].shape.data;
  // extract normal response
  if (normalChartDataSchema.safeParse(data).success) {
    const typedData = normalChartDataSchema.parse(data);

    const labels = Object.values(typedData)
      .map((d) => d.map((d) => d.time))
      .flat()
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
      })
      .reduce((acc: string[], curr: string) => {
        if (acc.includes(curr)) return acc;
        return [...acc, curr];
      }, []);
    const values = Object.entries(typedData).map(([key, value]) => ({
      key,
      value: labels.map((date) => {
        const found = value.find((d) => d.time === date);
        return found ? found.value : null;
      }),
    }));
    return { labels, values };
  }

  const peStreamResponseSchema =
    financialIndicatorResponseSchema.options[2].shape.data;
  // extract pe_stream response
  if (peStreamResponseSchema.safeParse(data).success) {
    const typedData = peStreamResponseSchema.parse(data);
    const firstData = Object.values(typedData).pop();
    if (firstData) {
      const { monthlyAvgPrice: monthlyAvgPriceData, ...peStreamData } =
        firstData;

      const labels = monthlyAvgPriceData.map((d) => d.date);

      const values = [
        {
          key: t.monthlyAvgPrice,
          value: monthlyAvgPriceData.map((d) => d.score),
        },
        ...Object.values(peStreamData).map((value) => ({
          key: value.text,
          value: labels.map((date) => {
            const found = value.data.find((d) => d.date === date);
            return found ? found.score : null;
          }),
        })),
      ];
      return { labels, values };
    }
  }

  // extract forecast response
  const forecastResponseSchema =
    financialIndicatorResponseSchema.options[1].shape.data;
  if (forecastResponseSchema.safeParse(data).success) {
    const typedData = forecastResponseSchema.parse(data);
    const firstData = Object.values(typedData).pop();
    if (firstData) {
      const labels = Object.values(firstData)
        .map((d) => d.map((d) => d.date))
        .flat()
        .sort((a, b) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA.getTime() - dateB.getTime();
        })
        .reduce((acc: string[], curr: string) => {
          if (acc.includes(curr)) return acc;
          return [...acc, curr];
        }, []);

      const values = Object.entries(firstData).map(([key, value]) => ({
        key: t.forecast[key as keyof typeof t.forecast],
        value: labels.map((date) => {
          const found = value.find((d) => d.date === date);
          return found ? found.value : null;
        }),
      }));

      return { labels, values };
    }
  }

  return { labels: [], values: [] };
};
